import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send } from 'lucide-react';
import { SYSTEM_PROMPT } from '../worker/chatbot';

const MAX_QUESTIONS = 3;

export default function ChatApp() {
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [questionsAsked, setQuestionsAsked] = useState(0);
  const [progress, setProgress] = useState(0);
  const [messages, setMessages] = useState([]);
  const honeypotRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    let timer;
    if (isLoading) {
      setProgress(0);
      timer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prev + 2;
        });
      }, 50);
    }
    return () => clearInterval(timer);
  }, [isLoading]);

  const getSecurityToken = async (email) => {
    const response = await fetch('https://api.developir.ai/chat/generate-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    if (!response.ok) {
      throw new Error('Failed to generate security token');
    }
    const { token } = await response.json();
    return token;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (questionsAsked >= MAX_QUESTIONS) {
        setError('You have reached your limit of 3 questions. Please contact us for more assistance.');
        return;
      }

      if (!message.trim() || !email.trim()) {
        setError('Please provide both email and message');
        return;
      }

      if (honeypotRef.current?.firstChild?.value) {
        setError('Invalid request');
        return;
      }

      setIsLoading(true);
      setMessages(prev => [...prev, { type: 'user', content: message }]);

      // Get security token
      const securityToken = await getSecurityToken(email);

      const response = await fetch('https://api.developir.ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message,
          email,
          securityToken,
          systemPrompt: SYSTEM_PROMPT  // Include the system prompt with each request
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send message');
      }

      const data = await response.json();
      setMessages(prev => [...prev, { type: 'assistant', content: data.message }]);
      setMessage('');
      setQuestionsAsked(prev => prev + 1);

    } catch (err) {
      setError(err.message || 'An error occurred');
      setMessages(prev => prev.slice(0, -1)); // Remove the last user message if there's an error
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  return (
    <div className="py-16 px-4 bg-brand-dark text-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 font-serif">
            AI Assistant
          </h2>
          <p className="text-xl max-w-3xl mx-auto text-gray-300">
            Ask questions about how technology can help your business grow
          </p>
        </div>
        
        <div className="rounded-lg border border-coral-900/50 max-w-4xl mx-auto bg-black/40 backdrop-blur-sm shadow-lg overflow-hidden">
          <div className="p-6">
            <div className="space-y-6">
              <div className="space-y-3 border-b border-white/10 pb-6">
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-8 w-8 text-coral-400" />
                  <h3 className="text-2xl font-semibold font-serif text-white">
                    Let's Talk Technology
                  </h3>
                </div>
                <p className="text-gray-300">
                  Ask up to 3 questions about technology solutions for your business.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-300">
                  <span>Questions Available</span>
                  <span>{MAX_QUESTIONS - questionsAsked} of {MAX_QUESTIONS}</span>
                </div>
                <div 
                  role="progressbar" 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                  aria-valuenow={isLoading ? progress : ((MAX_QUESTIONS - questionsAsked) / MAX_QUESTIONS) * 100}
                  className="h-2 bg-black/30 overflow-hidden rounded-full border border-coral-900/30"
                >
                  <div 
                    className="bg-coral-500 h-full transition-all duration-300"
                    style={{ 
                      width: isLoading 
                        ? `${progress}%`
                        : `${((MAX_QUESTIONS - questionsAsked) / MAX_QUESTIONS) * 100}%`
                    }}
                  />
                </div>
              </div>

              <div className="h-[400px] overflow-y-auto space-y-4 mb-4 p-4 bg-black/30 rounded-lg border border-coral-900/30">
                {messages.length === 0 ? (
                  <div className="text-gray-400 text-center py-4">
                    How can technology help your business grow?
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        msg.type === 'user' 
                          ? 'bg-coral-500 text-white' 
                          : 'bg-black/50 border border-coral-900/30 text-white'
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="space-y-4">
                {questionsAsked === 0 && (
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="w-full p-3 bg-black/30 border border-coral-900/30 text-white placeholder-gray-400 rounded-lg resize-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                    required
                  />
                )}
                <div className="flex gap-3">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What technology challenges is your business facing?"
                    rows="3"
                    className="flex-1 min-h-[80px] p-3 bg-black/30 border border-coral-900/30 text-white placeholder-gray-400 rounded-lg resize-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                    disabled={isLoading || questionsAsked >= MAX_QUESTIONS}
                  />
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading || questionsAsked >= MAX_QUESTIONS || !message.trim() || (!email && questionsAsked === 0)}
                    className="whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-20 w-20 rounded-full bg-coral-500 hover:bg-coral-600 text-white flex items-center justify-center p-0"
                  >
                    <Send className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-red-400 text-sm py-2">{error}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}